
import { UserSelector } from '@tencent/tds-ui';
import {
  message, Modal, Select,
} from 'antd';
import React, { useState } from 'react';
import IconClose from '@/assets/images/icons/icon-close.react.svg?react';
import { ReportGuideType } from '@/config/app/auth';
import { getUserId } from '@/infrastructure/auth';
import { getProductId } from '@/infrastructure/product-info';
import FreeProductGuide, { Display, GuideType } from '@/presentation/components/free-product-guide';
import { batchModifyIssueStatus } from '@/presentation/components/issue-tapd-enable-batch/api';
import { AddTagModal, ITagName } from '@/presentation/components/question-tag/add-tag-modal';
import { TagBaseType, TagInfo, TagSubType } from '@/presentation/components/question-tag/api';
import { addTag } from '@/presentation/pages/setting/tag-manage/api';
import {
  Zone,
  ISSUE_STATUS_OPTION,
  FIELD_ALL,
} from '@/proto/field';
import { basicService } from '@/service/basic';
import styles from './index.module.scss';
import { ReportField, ReportScene } from '@/infrastructure/reporter/datong/report';

type IProps<T> = {
  type: Zone;
  rows: T[];
  onSubmit: () => void;
};

export const IssueTapdStatusBatch = <T extends Record<string, any>>(props: IProps<T>) => {
  const { type, rows, onSubmit } = props;

  const statusOptions = ISSUE_STATUS_OPTION.filter(item => item.value !== FIELD_ALL);

  const [visible, setVisible] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [status, setStatus] = useState<string>(statusOptions[0].value);

  const onOk = async () => {
    setLoading(true);
    const succ = await batchModifyIssueStatus({
      type,
      issueKeys: rows.map(row => row.feature || row.issueId),
      status,
      productId: getProductId(),
      operator: getUserId(),
    });
    setLoading(false);

    if (succ) {
      onCancel();
      message.success('修改处理状态成功');
      onSubmit();
    } else {
      message.error('修改处理状态失败');
    }
  };


  const onCancel = () => {
    setLoading(false);
    setVisible(false);
    setStatus(statusOptions[0].value);
  };

  const showModal = () => {
    setVisible(true);
  };

  return <>
    <div onClick={showModal}>修改处理状态</div>
    <Modal
      onOk={onOk}
      destroyOnClose
      maskClosable={false}
      closeIcon={<IconClose/>}
      onCancel={onCancel}
      cancelButtonProps={{
        className: styles.cancelButton,
        disabled: loading,
      }}

      okButtonProps={{
        loading,
        className: 'bg-[#0A6CFF] rounded-[3px]',
      }}
      width={600}
      visible={visible}
      title={'批量设置处理状态'}
      className={styles.modal}>
      <p>批量修改处理状态，将直接修改选中Issue的处理状态。当前选中的Issue中，有部分已处其他状态，请确认是否要统一调整</p>
        <Select onChange={setStatus} value={status} className={'w-[130px]'} options={statusOptions} />
    </Modal>
  </>;
};

const handlerOptions = [
  { value: 'append', label: '追加' },
  { value: 'overwrite', label: '覆盖' },
];

export const IssueTapdHandlerBatch = <T extends Record<string, any>>(props: IProps<T>) => {
  const { type, rows, onSubmit } = props;
  const [owners, setOwners] = useState<string[]>([]);
  const [visible, setVisible] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [handlerMode, setHandlerMode] = useState<string>(handlerOptions[0].value);
  const onOk = async () => {
    setLoading(true);
    const succ = await batchModifyIssueStatus({
      type,
      issueKeys: rows.map(row => row.feature || row.issueId),
      tapdHandler: owners?.join?.(';'),
      productId: getProductId(),
      operator: getUserId(),
      handlerMode,
    });
    setLoading(false);
    if (succ) {
      onCancel();
      message.success('修改处理人成功');
      onSubmit();
    } else {
      message.error('修改处理人失败');
    }
  };

  const onCancel = () => {
    setLoading(false);
    setVisible(false);
    setOwners([]);
    setHandlerMode(handlerOptions[0].value);
  };

  const showModal = () => {
    setVisible(true);
  };

  return <>
    <div onClick={showModal}>设置处理人</div>
    <Modal
      onOk={onOk}
      destroyOnClose
      maskClosable={false}
      closeIcon={<IconClose/>}
      onCancel={onCancel}
      cancelButtonProps={{
        className: styles.cancelButton,
        disabled: loading,
      }}

      okButtonProps={{
        loading,
        className: 'bg-[#0A6CFF] rounded-[3px]',
      }}
      width={600}
      visible={visible}
      title={'批量设置处理人'}
      className={styles.modal}>
      <p>追加：在Issue原处理人的基础上，追加新的处理人。</p>
      <p>覆盖：直接用新的处理人，覆盖Issue原处理人。</p>
      <div className={'flex gap-[8px]'}>
        <Select onChange={setHandlerMode} value={handlerMode} className={'w-[86px]'} options={handlerOptions} />
        <UserSelector
          className={styles.ownerEdit}
          placeholder=""
          mode={'tags'}
          showSearch={true}
          suffixIcon={<></>}
          autoFocus={true}
          value={owners}
          onChange={setOwners}
        />
      </div>
    </Modal>
  </>;
};

interface ITagBatch<T> {
  enableBatchTag: {
    baseType: TagBaseType,
    subType: TagSubType,
  }
  rows: T[];
  onSubmit: () => void;
}

export const IssueTagBatch = <T extends Record<string, any>>(props: ITagBatch<T>) => {
  const { enableBatchTag, rows, onSubmit } = props;
  const [visible, setVisible] = useState<boolean>(false);

  const onOk = async (tagNames: ITagName[]) => {
    if (tagNames.length) {
      const haveIdTags: TagInfo[] = [];
      const noIdTags: TagInfo[] = [];
      tagNames.forEach((item) => {
        if (item.tagId) {
          haveIdTags.push({ tagName: item.value, tagId: item.tagId, tagColor: item.color });
        } else {
          noIdTags.push({ tagName: item.value, tagId: item?.tagId || '', tagColor: item.color });
        }
      });
      const addIdTags = await Promise.all(noIdTags.map(async (item) => {
        const addIssueTag = await addTag(item.tagName, item.tagColor);
        return {
          ...item,
          tagId: addIssueTag?.data.tagID,
        };
      }));

      const res = await basicService.batchBindTagToFeature({
        baseType: enableBatchTag.baseType,
        subType: enableBatchTag.subType,
        bindTagReqItem: rows?.map(row => ({
          feature: row.feature || row.issueId,
          tagIds: [...new Set([
            ...(row.tagInfo || row.issueFeature.tagList || []).map((item: { tagId: any; }) => item.tagId),
            ...(haveIdTags?.map?.(item => item.tagId!) || []),
            ...(addIdTags?.map?.(item => item.tagId!) || []),
          ])],
        })),
      });
      if (res) {
        setVisible(false);
        message.success('已添加标签');
        onSubmit?.();
      }
    } else if (!tagNames.length) {
      message.error('请输入需要添加的标签');
      setVisible(true);
    } else {
      message.error('添加标签重复，请重试');
      setVisible(true);
    }
  };

  const showModal = () => {
    setVisible(true);
  };

  return <>
    <FreeProductGuide className={'text-[#d9d9d9]'} guideType={GuideType.POP_CONFIRM} display={Display.INLINE}
      reportGuideType={ReportGuideType.Bubble}
      reportScene={ReportScene.showModal}
      reportField={ReportField.showModal}>
      <div onClick={showModal}>添加问题标签</div>
    </FreeProductGuide>
    <AddTagModal
      visible={visible}
      setModalVisible={setVisible}
      onSubmit={onOk}
    />
  </>;
};
